/**
 * Shared utilities and constants for the Projects/Kanban feature.
 */

export type ProjectStatus =
  | 'ACTIVE'
  | 'ON_HOLD'
  | 'COMPLETED'
  | 'CANCELLED'
  | 'ARCHIVED';

export type TaskPriority = 'LOW' | 'NORMAL' | 'HIGH' | 'URGENT';

export const PROJECT_STATUS_LABELS: Record<ProjectStatus, string> = {
  ACTIVE: 'Aktywny',
  ON_HOLD: 'Wstrzymany',
  COMPLETED: 'Zakończony',
  CANCELLED: 'Anulowany',
  ARCHIVED: 'Zarchiwizowany',
};

/**
 * Tailwind classes for status pill (background + text color).
 */
export const PROJECT_STATUS_CLASSES: Record<ProjectStatus, string> = {
  ACTIVE:
    'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-200',
  ON_HOLD:
    'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/40 dark:text-yellow-200',
  COMPLETED:
    'bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-200',
  CANCELLED:
    'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-200',
  ARCHIVED: 'bg-muted text-muted-foreground',
};

export const PROJECT_STATUS_OPTIONS: ProjectStatus[] = [
  'ACTIVE',
  'ON_HOLD',
  'COMPLETED',
  'CANCELLED',
  'ARCHIVED',
];

export const TASK_PRIORITY_LABELS: Record<TaskPriority, string> = {
  LOW: 'Niski',
  NORMAL: 'Normalny',
  HIGH: 'Wysoki',
  URGENT: 'Pilny',
};

export const TASK_PRIORITY_CLASSES: Record<TaskPriority, string> = {
  LOW: 'bg-gray-200 text-gray-800 dark:bg-gray-700 dark:text-gray-100',
  NORMAL: 'bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-200',
  HIGH:
    'bg-orange-100 text-orange-800 dark:bg-orange-900/40 dark:text-orange-200',
  URGENT:
    'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-200',
};

export const TASK_PRIORITY_OPTIONS: TaskPriority[] = [
  'LOW',
  'NORMAL',
  'HIGH',
  'URGENT',
];

/**
 * Format a YYYY-MM-DD or ISO date for display in pl-PL locale.
 * Returns '-' for missing values.
 */
export function formatDate(value?: string | null): string {
  if (!value) return '-';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '-';
  return d.toLocaleDateString('pl-PL');
}

/**
 * Convert an ISO timestamp / DateTime value to a YYYY-MM-DD string for <input type="date">.
 */
export function toDateInputValue(value?: string | null): string {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}

/**
 * Compare two employees alphabetically by lastName, then firstName, using Polish locale.
 * Use as a default sorter for any employee list / dropdown.
 */
export function compareEmployeesByLastName<
  T extends { firstName?: string | null; lastName?: string | null },
>(a: T, b: T): number {
  const lastCmp = (a.lastName || '').localeCompare(b.lastName || '', 'pl');
  if (lastCmp !== 0) return lastCmp;
  return (a.firstName || '').localeCompare(b.firstName || '', 'pl');
}

export function getInitials(firstName?: string, lastName?: string): string {
  const f = (firstName || '').trim();
  const l = (lastName || '').trim();
  return ((f.charAt(0) || '') + (l.charAt(0) || '')).toUpperCase() || '?';
}

/**
 * Compute due-date colouring relative to today.
 * Returns Tailwind text color classes.
 */
export function getDueDateColorClass(plannedEndDate?: string | null): string {
  if (!plannedEndDate) return 'text-muted-foreground';
  const due = new Date(plannedEndDate);
  if (Number.isNaN(due.getTime())) return 'text-muted-foreground';
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const dueDay = new Date(due);
  dueDay.setHours(0, 0, 0, 0);
  const diff = dueDay.getTime() - today.getTime();
  if (diff < 0) return 'text-red-600 dark:text-red-400';
  if (diff === 0) return 'text-orange-600 dark:text-orange-400';
  return 'text-muted-foreground';
}
